/**
 * Pure catalog logic for ResourceCatalog (GDN-2335). No React, no MUI —
 * every function is resource-agnostic and unit-testable in isolation.
 */
import dayjs from 'dayjs';

import type {
  CatalogConfirmSummary,
  CatalogFilterState,
  CatalogRow,
  CatalogSortKey,
  CatalogSortState,
} from './ResourceCatalog.types';

/** Ids from a selection, split by the archived flag of their rows. */
export interface CatalogSelectionSplit {
  activeIds: string[];
  archivedIds: string[];
}

/**
 * Rows surviving the current filter state. Archived rows are hidden unless
 * showArchived is on; a non-empty owner selection matches only rows whose
 * ownerLabel is selected (shared rows have no owner, so they drop out).
 */
export function applyCatalogFilters(
  rows: CatalogRow[],
  filters: CatalogFilterState
): CatalogRow[] {
  const search = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (!filters.showArchived && row.archived) return false;
    if (filters.scope !== 'all' && row.scope !== filters.scope) return false;
    if (
      filters.owners.length > 0 &&
      (row.ownerLabel === null || !filters.owners.includes(row.ownerLabel))
    ) {
      return false;
    }
    if (search !== '' && !row.name.toLowerCase().includes(search)) {
      return false;
    }
    return true;
  });
}

function compareByKey(a: CatalogRow, b: CatalogRow, key: CatalogSortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    case 'usage':
      return a.usage - b.usage;
    case 'lastUsedAt': {
      const diff =
        Date.parse(a.lastUsedAt ?? '') - Date.parse(b.lastUsedAt ?? '');
      // Malformed non-null dates yield NaN, which would make the order
      // implementation-defined — treat them as equal instead (stable sort
      // keeps them where they were).
      return Number.isNaN(diff) ? 0 : diff;
    }
  }
}

/**
 * Stable sort (does not mutate the input). For lastUsedAt, null rows sort
 * last regardless of direction — "never used" is neither oldest nor newest.
 */
export function sortCatalogRows(
  rows: CatalogRow[],
  sort: CatalogSortState
): CatalogRow[] {
  const dir = sort.direction === 'asc' ? 1 : -1;
  // Array.prototype.sort is spec-stable; copy first to stay pure.
  return [...rows].sort((a, b) => {
    if (sort.key === 'lastUsedAt') {
      // Nulls-last is absolute — it ignores the sort direction.
      if (a.lastUsedAt === null && b.lastUsedAt === null) return 0;
      if (a.lastUsedAt === null) return 1;
      if (b.lastUsedAt === null) return -1;
    }
    return dir * compareByKey(a, b, sort.key);
  });
}

/** Distinct ownerLabel values across rows, sorted — the owner filter facet. */
export function collectOwnerOptions(rows: CatalogRow[]): string[] {
  const owners = new Set<string>();
  for (const row of rows) {
    if (row.ownerLabel !== null) owners.add(row.ownerLabel);
  }
  return [...owners].sort((a, b) => a.localeCompare(b));
}

/** Selection restricted to currently visible rows — never act on hidden rows. */
export function pruneSelection(
  visibleRows: CatalogRow[],
  selectedIds: string[]
): string[] {
  const visible = new Set(visibleRows.map((row) => row.id));
  return selectedIds.filter((id) => visible.has(id));
}

/**
 * Split a selection into archivable (active) and restorable (archived) ids —
 * a mixed selection drives both bulk buttons, each acting on its subset.
 */
export function splitSelection(
  rows: CatalogRow[],
  selectedIds: string[]
): CatalogSelectionSplit {
  const selected = new Set(selectedIds);
  const activeIds: string[] = [];
  const archivedIds: string[] = [];
  for (const row of rows) {
    if (!selected.has(row.id)) continue;
    if (row.archived) {
      archivedIds.push(row.id);
    } else {
      activeIds.push(row.id);
    }
  }
  return { activeIds, archivedIds };
}

/** True when lastUsedAt is in the future — scheduled, not-yet-happened use. */
export function isUpcoming(iso: string | null, now: Date = new Date()): boolean {
  if (iso === null) return false;
  return dayjs(iso).isAfter(dayjs(now));
}

/** Aggregates for the confirm dialog. Ids without a matching row are ignored. */
export function summarizeSelection(
  rows: CatalogRow[],
  ids: string[],
  highUsageThreshold: number,
  now: Date = new Date()
): CatalogConfirmSummary {
  const selected = new Set(ids);
  let count = 0;
  let totalUsage = 0;
  let sharedCount = 0;
  let highUsageCount = 0;
  let upcomingCount = 0;
  for (const row of rows) {
    if (!selected.has(row.id)) continue;
    count += 1;
    totalUsage += row.usage;
    if (row.scope === 'shared') sharedCount += 1;
    if (row.usage >= highUsageThreshold) highUsageCount += 1;
    if (isUpcoming(row.lastUsedAt, now)) upcomingCount += 1;
  }
  return { count, totalUsage, sharedCount, highUsageCount, upcomingCount };
}

/**
 * The result-count line above the table: total when nothing narrows the
 * view, "Showing X of Y" when filters do, plus a hidden-archived hint so
 * the archived toggle is discoverable. The denominator respects the
 * archived toggle — archived rows only count when they're shown.
 */
export function describeVisibleCount(
  rows: CatalogRow[],
  visibleRows: CatalogRow[],
  filters: CatalogFilterState,
  nounPlural: string
): string {
  const base = filters.showArchived
    ? rows.length
    : rows.filter((row) => !row.archived).length;
  const archivedCount = rows.length - rows.filter((row) => !row.archived).length;

  const head =
    visibleRows.length === base
      ? `${base.toLocaleString()} ${nounPlural}`
      : `Showing ${visibleRows.length.toLocaleString()} of ${base.toLocaleString()} ${nounPlural}`;

  if (!filters.showArchived && archivedCount > 0) {
    return `${head} · ${archivedCount.toLocaleString()} archived`;
  }
  return head;
}

/**
 * "Never" for null, "Upcoming" for future dates (deliberate — upcoming
 * scheduled use), otherwise a compact date.
 */
export function formatLastUsed(iso: string | null, now: Date = new Date()): string {
  if (iso === null) return 'Never';
  if (isUpcoming(iso, now)) return 'Upcoming';
  return dayjs(iso).format('MMM D, YYYY');
}
